// Finsemble Central Logger Service -- the top-level file

import { BaseService } from "../baseService";
import { Logger } from "../../clients/logger";
import { ConfigClient } from "../../clients/configClient";
import { RouterClient } from "../../clients/routerClient";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { toast } from "../../common/toast";
import SystemManagerClient from "../../common/systemManagerClient";
/* no-restricted-imports is for ensuring cross-package imports use exported members.  Here there is a valid src subdirectory */
import LoggerStore from "./src/stores/LoggerStore";
import { LogMessage, CentralLoggerClientState, RegisterLoggerClient, UnregisterLoggerClient, LogLevels } from "./types";
import { FEALogLevel } from "../../FEA";
import { defaultLogLevels } from "./src/stores/State";
import { FEAGlobals } from "../../common";
import { Manifest } from "../../types";
import { System } from "../../common/system";

/**
 * LoggerService supports efficient and configurable run-time logging.
 */
class LoggerService extends BaseService {
	logToDiskLevels: FEALogLevel[] = [];

	/**
	 * @param {string} params.name name of the service
	 * @param {object} params.startupDependencies
	 * @param {array} params.startupDependencies.clients Clients to wait on before initializing the service
	 * @param {object} params.shutdownDependencies
	 * @param {array} params.shutdownDependencies.services Services to wait on before shutting down the service
	 * */
	constructor() {
		super({
			name: "loggerService",
			startupDependencies: {},
			shutdownDependencies: {
				services: [
					"workspaceService",
					"windowService",
					"linkerService",
					"interopService",
					"storageService",
					"configService",
					"dataStoreService",
					"authenticationService",
					"assimilationService",
				],
			},
		});

		this.onBaseServiceReady(async (callback) => {
			await this.initialize();
			this.createRouterEndpoints();
			Logger.start();

			callback();
		});
	}

	async initialize(): Promise<void> {
		const { err, data: manifest } = await ConfigUtil.getInitialManifest();
		if (!err && manifest) {
			this.maybeSetLogLevelsFromManifest(manifest);
			this.maybeSetLogLevelsToDisk(manifest);
			this.maybeStartLoggingFromFEA(manifest);
		}
		this.getLogLevelsToThrowErrorsFor().then(() => {
			// The FEA logger is waiting for the loggerService to finish initialization
			// When it receives this message it will begin transmitting FEA logs
			// Don't use await for getLogLevelsToThrowErrorsFor to prevent blocking initialization pipeline
			System.notifyLoggerReady();
		});
	}

	/**
	 * Creates the public interface with the rest of the application.
	 * Each of the handlers below simply receive a request, pass it off to a member function that does the work, and then sends the result back to the function that sent the original message (if necessary).
	 * @memberof LoggerService
	 */
	createRouterEndpoints() {
		RouterClient.addListener("CentralConsole-Show", () => {
			(window as any).showConsole();
		});
		RouterClient.addResponder<UnregisterLoggerClient, { status: "success" }>(
			"logger.service.unregister",
			(error, query) => {
				if (error || !query) {
					console.error(error);
				} else {
					LoggerStore.unRegisterClient(query.data);
					query.sendQueryResponse(null, { status: "success" });
				}
			}
		);
		RouterClient.addResponder<RegisterLoggerClient, CentralLoggerClientState>(
			"logger.service.register",
			async (error, query) => {
				if (error || !query) {
					console.error(error);
				} else {
					const loggerState = LoggerStore.onClientRegistered(query.data);
					query.sendQueryResponse(null, loggerState);
				}
			}
		);
		RouterClient.addListener<LogMessage[]>("logger.service.logMessages", (error, message) => {
			if (error) console.error(error);
			const { data } = message;
			data?.forEach((logMessage) => {
				this.maybeLogToDisk(logMessage);
			});
			LoggerStore.onNewMessage(data);
		});

		// For both restart and shutdown, cleanup the client list otherwise it will continue getting larger, to the extent it causes startup delays.
		// A reliable time to do this cleanup is at the START of a restart or shutdown request, hence the shutdown and restart listeners added here.
		// This cleanup cannot occur during the normal shutdown handshake because some clients have already unregistered as part of the shutdown or restart.
		RouterClient.addListener("Application.shutdown", () => {
			LoggerStore.removeUnregisteredClientsFromPersistState();
		});
		RouterClient.addListener("Application.restart", () => {
			LoggerStore.removeUnregisteredClientsFromPersistState();
		});

		// Relays state (e.g. logging level in Central Logger UI) changes back to FEA. (because FEA can't listen
		// directly on the router)
		RouterClient.addListener("finsemble.logger.client.finsemble-electron-adapter", (error, message) => {
			System.updateCentralLoggerState(message.data);
		});
	}

	/**
	 * Sends incoming messages to the FEA log if the message category is one
	 * which is set in `finsemble.logger.logToDiskLevels` array in config.
	 *
	 * Note: To prevent double logging, don't use this function for any messages that originate from FEA
	 *
	 * @param message A log message received from a window
	 */
	maybeLogToDisk(message: LogMessage) {
		const feaLogLevel = message.logType.toLowerCase() as FEALogLevel;
		if (this.logToDiskLevels.includes(feaLogLevel)) {
			FEAGlobals.fin.desktop.System.logToDisk(feaLogLevel, "Central Logger", message);
		}
	}

	/**
	 * Sets the levels to log to FEA log (disk) based on whether a config setting
	 * has been set for `finsemble.logger.logToDiskLevels`
	 *
	 * @param manifest The system manifest
	 */
	maybeSetLogLevelsToDisk(manifest: Manifest) {
		this.logToDiskLevels =
			manifest.finsemble?.logger?.logToDiskLevels ??
			manifest.finsemble?.system?.requiredServicesConfig?.loggerService?.logToDiskLevels ??
			[];
	}

	/**
	 * Sets the initial default central logger log levels based on what is set in
	 * config `logger.defaultClientLogLevels`. This function is bypassed if log levels
	 * have already been set by the end user (persisted).
	 *
	 * @param manifest The system manifest
	 */
	maybeSetLogLevelsFromManifest(manifest: Manifest) {
		const isPersisted = LoggerStore.persistState.get("isPersisted");

		// If the logging levels have already been persisted then move on
		if (isPersisted) return;

		// Otherwise, initialize state based on manifest settings (and set all clients to those levels)

		const configuredLogLevels =
			manifest.finsemble?.logger?.defaultClientLogLevels ??
			manifest.finsemble?.system?.requiredServicesConfig?.loggerService?.defaultClientLogLevels ??
			defaultLogLevels;

		LoggerStore.persistState.set("initialClientStateDefault", configuredLogLevels);
		LoggerStore.emit("newInitialClientStateDefault");

		const logLevels = {
			console: configuredLogLevels,
			system: configuredLogLevels,
			perf: configuredLogLevels,
			dev: configuredLogLevels,
		};

		// initialize client states with default log levels
		let clients: LogLevels[] = LoggerStore.persistState.get("clientState");
		Object.keys(clients).forEach((key) => {
			clients[key] = {
				...logLevels,
				...clients[key],
			};
		});
		LoggerStore.persistState.set("clientState", clients);
		LoggerStore.emit("newClientState");

		LoggerStore.persistState.set("isPersisted", true);
	}

	/**
	 * Creates finsemble-electron-adapter logger client
	 * if the `manifest.finsemble-electron-adapter.logger.transmitToCentralLogger` is true.
	 *
	 * @param manifest The system manifest
	 */
	maybeStartLoggingFromFEA(manifest: Manifest) {
		if (manifest["finsemble-electron-adapter"]?.logger?.transmitToCentralLogger === true) {
			const loggerDisplayName = "Finsemble Electron Adapter";
			const loggerClientName = "finsembleElectronAdapter";

			const convertLoggerServiceMessageToFEALogMessage = (message: any) => {
				const logMessage: LogMessage = {
					category: "system",
					logClientName: loggerClientName,
					logType: message.level,
					logData: message.logData,
					logTimestamp: message.time,
				};
				return logMessage;
			};

			// A listener to handle FEA log events.
			System.addEventListener("log-fea", (message: any) => {
				// Don't use the `maybeLogToDisk` function for any messages from FEA to prevent double logging.
				if (Array.isArray(message)) {
					LoggerStore.onNewMessage(message.map((m) => convertLoggerServiceMessageToFEALogMessage(m)));
				} else {
					LoggerStore.onNewMessage([convertLoggerServiceMessageToFEALogMessage(message)]);
				}
			});

			const loggerState = LoggerStore.onClientRegistered({
				centralLoggerNamePrefix: "",
				clientChannel: "finsemble.logger.client.finsemble-electron-adapter",
				clientName: loggerClientName,
				displayName: loggerDisplayName,
				viewId: null,
				windowName: "System Manager",
			});
			System.updateCentralLoggerState(loggerState);
		}
	}

	// Figures out whether to throw notifications if certain log-levels are set.
	async getLogLevelsToThrowErrorsFor() {
		// don't want to depend on configService for logger startup, so async wait here for config service to come up
		await SystemManagerClient.waitForStartup("configService");
		const { err, data: config } = await ConfigClient.getValue({ field: "finsemble" });
		if (err) return;

		const isHighLoggingConfiguration = (logLevels, client): boolean =>
			logLevels.some((level) => client.system?.[level] === true);

		// Derive which log levels are considered to be high volume
		const highVolumeLoggingLevels =
			config?.servicesConfig?.logger?.showWarningForLogStates ??
			config?.system?.requiredServicesConfig?.loggerService?.showWarningForLogStates ??
			[];

		const clients = LoggerStore.persistState.get("clientState") ?? [];
		Logger.system.log("Checking for high logging level warning", highVolumeLoggingLevels, clients);

		const isSetForHighVolumeLogging = Object.values(clients).some((client) =>
			isHighLoggingConfiguration(highVolumeLoggingLevels, client)
		);

		if (isSetForHighVolumeLogging) {
			const message =
				"Warning: diagnostic logging detected, which can slow performance. Check that 'info', 'debug', `verbose` are off in the central logger.";
			toast("dev", "ONCE-SINCE-STARTUP", "Unnecessary-Logging-States-Error", message, {
				duration: 5000,
			});
		}
	}
}

export default new LoggerService();
